/* eslint-disable max-lines */
import { reject } from 'ramda';
import * as types from '../mutation-types';
import { apolloClient } from '@/apollo';
import {
  UserProjectsDocument,
  CreateProjectDocument,
  CreateProjectExportDocument,
  CreateProjectFromTemplateDocument,
  UpdateProjectDocument,
  DeleteProjectDocument,
  DeleteProjectPermanentlyDocument,
  RestoreProjectDocument,
  InviteToProjectDocument,
  ResendInviteToProjectDocument,
  DeleteInviteToProjectDocument,
  DeleteTeamMemberFromProjectDocument,
  TransferProjectOwnershipToTeamMemberDocument,
  ProjectTemplateDocument,
  ProjectInvitationsFragmentDoc,
  ProjectInvitationsFragment,
  UserProjectsQueryVariables,
  UserProjectsQuery,
} from '@/graphql/operations.gen';
import { getTracker } from '@/tracking/trackingPlugin';
import { ProjectEvent, TrackingProperty } from '@/tracking/trackingTypes';
import { RootState } from '../types';
import i18n from '@/i18n';
import { Action } from 'vuex';
import { ApolloCache } from '@apollo/client/cache';

const updateUserProjectsInCache = (
  store: ApolloCache<any>,
  updateFn: (user: UserProjectsQuery['user']) => Partial<UserProjectsQuery['user']>
) => {
  const cacheData = store.readQuery<UserProjectsQuery, UserProjectsQueryVariables>({
    query: UserProjectsDocument,
  });

  if (cacheData) {
    // cache may not be available - query is only loaded in project overview

    store.writeQuery<UserProjectsQuery, UserProjectsQueryVariables>({
      query: UserProjectsDocument,
      data: {
        user: {
          ...cacheData.user,
          ...updateFn(cacheData.user),
        },
      },
    });
  }
};

// initial state
const state = {};

// getters
const getters = {};

// actions
const actions: { [key: string]: Action<{}, RootState> } = {
  /**
   * Create project.
   *
   */
  create({ commit, dispatch }, data) {
    commit(types.PROJECT_CREATE, {
      meta: {
        analytics: [['event', 'project', 'create']],
      },
    });

    return apolloClient
      .mutate({
        mutation: CreateProjectDocument,
        variables: { ...data },
        update(store, response) {
          updateUserProjectsInCache(store, (user) => ({
            projects: [...user.projects, response.data.createProject],
          }));

          return response;
        },
      })
      .then((response) => {
        const project = response.data.createProject;
        getTracker().track(ProjectEvent.ProjectCreated, {
          [TrackingProperty.HasDescription]: Boolean(project.description),
          [TrackingProperty.Color]: project.color,
        });

        dispatch(
          'notification/add',
          { message: i18n.global.t('notification.projectCreated', response.data.createProject) },
          { root: true }
        );

        return response.data.createProject;
      });
  },

  createFromTemplate(context, { json }) {
    return apolloClient
      .mutate({
        mutation: CreateProjectFromTemplateDocument,
        variables: { json },
        update(store, response) {
          updateUserProjectsInCache(store, (user) => ({
            ...user,
            projects: [...user.projects, response.data.createProjectFromTemplate],
          }));

          return response;
        },
      })
      .then((data) => data.data.createProjectFromTemplate);
  },

  clone({ commit, dispatch }, { id }) {
    commit(types.PROJECT_DUPLICATE, {
      meta: {
        analytics: [['event', 'project', 'duplicate']],
      },
    });

    return apolloClient
      .query({
        query: ProjectTemplateDocument,
        variables: { id },
      })
      .then((response) => JSON.stringify(response.data.project))
      .then((json) => dispatch('project/createFromTemplate', { json }, { root: true }))
      .then((newProject) => {
        getTracker().track(ProjectEvent.ProjectDuplicated);

        dispatch(
          'notification/add',
          { message: i18n.global.t('notification.projectDuplicated', newProject) },
          { root: true }
        );

        return newProject;
      });
  },

  /**
   * Update project.
   *
   */
  update({ commit }, data) {
    commit(types.PROJECT_UPDATE, {
      meta: {
        analytics: [['event', 'project', 'update']],
      },
    });

    return apolloClient.mutate({
      mutation: UpdateProjectDocument,
      variables: {
        ...data,
      },
    });
  },

  /**
   * Delete project.
   */
  delete({ commit }, { id }) {
    commit(types.PROJECT_DELETE, {
      meta: {
        analytics: [['event', 'project', 'delete']],
      },
    });

    return apolloClient.mutate({
      mutation: DeleteProjectDocument,
      variables: {
        id,
      },
      update(cache, response) {
        cache.evict({ id: `Project:${id}` });

        return response;
      },
    });
  },

  restore({}, { id }) {
    return apolloClient.mutate({
      mutation: RestoreProjectDocument,
      variables: {
        id,
      },
      update(store, response) {
        updateUserProjectsInCache(store, (user) => ({
          projects: [...user.projects, response.data.restoreProject],
        }));

        return response;
      },
    });
  },

  createExport(_root, variables) {
    return apolloClient.mutate({
      mutation: CreateProjectExportDocument,
      variables,
    });
  },

  async downloadExport(_root, { exportLocation }) {
    const response = await fetch(exportLocation, {
      method: 'GET',
    });

    if (response.ok) {
      return response.blob();
    }

    return response.json().then((json) => Promise.reject(json));
  },

  invite(_context, variables) {
    return apolloClient.mutate({
      mutation: InviteToProjectDocument,
      variables,
      update(store, response) {
        const responseData = response.data.inviteToProject;

        const cacheId = `Project:${variables.id}`;

        const cacheData = store.readFragment<ProjectInvitationsFragment>({
          fragment: ProjectInvitationsFragmentDoc,
          fragmentName: 'ProjectInvitations',
          id: cacheId,
        });

        store.writeFragment<ProjectInvitationsFragment>({
          fragment: ProjectInvitationsFragmentDoc,
          fragmentName: 'ProjectInvitations',
          id: cacheId,
          data: {
            // If we are inviting from the Projec Overview, invitations might be undefined
            invitations: cacheData ? [...cacheData.invitations, responseData] : [responseData],
          },
        });
      },
    });
  },

  resendInvite(_context, variables) {
    return apolloClient.mutate({
      mutation: ResendInviteToProjectDocument,
      variables,
    });
  },

  deleteInvite(_context, variables) {
    return apolloClient.mutate({
      mutation: DeleteInviteToProjectDocument,
      variables,
      update(store) {
        const cacheId = `Project:${variables.id}`;

        const cacheData = store.readFragment<ProjectInvitationsFragment>({
          fragment: ProjectInvitationsFragmentDoc,
          fragmentName: 'ProjectInvitations',
          id: cacheId,
        });

        store.writeFragment<ProjectInvitationsFragment>({
          fragment: ProjectInvitationsFragmentDoc,
          fragmentName: 'ProjectInvitations',
          id: cacheId,
          data: {
            invitations: reject((invite) => invite.id === variables.inviteId, cacheData.invitations),
          },
        });
      },
    });
  },

  removeTeamMember(_context, { id, teamMemberId }) {
    return apolloClient.mutate({
      mutation: DeleteTeamMemberFromProjectDocument,
      variables: { id, teamMemberId },
    });
  },

  transferOwnership(_context, { id, teamMemberId }) {
    return apolloClient.mutate({
      mutation: TransferProjectOwnershipToTeamMemberDocument,
      variables: { id, teamMemberId },
      refetchQueries: ['User', 'Project'],
      awaitRefetchQueries: true,
    });
  },
};

if (process.env.NODE_ENV !== 'production') {
  /**
   * Delete project permanently
   */
  actions.deletePermanently = ({ commit, dispatch }, { id, name }) => {
    commit(types.PROJECT_DELETE, {
      meta: {
        analytics: [['event', 'project', 'delete']],
      },
    });

    return apolloClient
      .mutate({
        mutation: DeleteProjectPermanentlyDocument,
        variables: {
          id,
        },
        update(store, response) {
          updateUserProjectsInCache(store, (user) => ({
            projects: reject((project) => project.id === id, user.projects),
          }));

          return response;
        },
      })
      .then(() => {
        dispatch(
          'notification/add',
          {
            message: i18n.global.t('notification.projectSuccessfullyDeleted', { name }),
          },
          { root: true }
        );
      });
  };
}

// mutations
const mutations = {
  [types.PROJECT_CREATE]() {},

  [types.PROJECT_DUPLICATE]() {},

  [types.PROJECT_UPDATE]() {},

  [types.PROJECT_DELETE]() {},
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
